/* Direct-API Cognito auth — no hosted UI, no redirects.
 *
 * The admin login form on /admin posts the email + password to
 * Cognito's API directly using SRP (Secure Remote Password). The
 * exchange happens entirely in the browser; no Cognito-hosted
 * splash page in between, so we get to render the login experience
 * with the rest of the site's theme.
 *
 * Token format and verification path on the backend are unchanged:
 * Cognito issues a JWT, the backend verifies it via aws-jwt-verify.
 *
 * Flows handled here:
 *   - login(email, password)
 *       → returns { ok: true } on success, OR { challenge: 'NEW_PASSWORD_REQUIRED', user }
 *         when the user was just invited (first sign-in must set a real password).
 *   - completeNewPassword(user, newPassword) — finishes the first-time login.
 *   - signOut() — clears the local session and Cognito's local cache.
 *   - getIdToken(), isLoggedIn() — read current session.
 *   - forgotPassword(email) + confirmForgotPassword(email, code, newPw)
 *     for self-serve password reset.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

let _cachedConfig = null;
let _cachedPool = null;

async function getConfig() {
  if (_cachedConfig) return _cachedConfig;
  const res = await fetch('/api/admin/config');
  if (!res.ok) throw new Error('Failed to load auth config');
  const { cognito } = await res.json();
  if (!cognito || !cognito.userPoolId || !cognito.clientId) {
    throw new Error('Cognito is not configured on this deployment');
  }
  _cachedConfig = cognito;
  return cognito;
}

async function getPool() {
  if (_cachedPool) return _cachedPool;
  const cfg = await getConfig();
  _cachedPool = new CognitoUserPool({
    UserPoolId: cfg.userPoolId,
    ClientId: cfg.clientId,
  });
  return _cachedPool;
}

/* Sign in with email + password.
 *
 * Returns one of:
 *   { ok: true, session }
 *     — sign-in succeeded. Session is cached on the CognitoUser; we
 *       extract the ID token via getIdToken() on subsequent calls.
 *   { challenge: 'NEW_PASSWORD_REQUIRED', user }
 *     — Cognito invited this user with a temp password and they must
 *       set a real one before the session is valid. Pass the returned
 *       `user` to completeNewPassword().
 *   { challenge: 'MFA_SETUP', user }
 *     — Pool requires MFA and this user hasn't enrolled. Caller hands
 *       the `user` to beginMfaSetup() to fetch the TOTP secret +
 *       otpauth URI, then to verifyMfaSetup() with the customer's
 *       first 6-digit code.
 *   { challenge: 'SOFTWARE_TOKEN_MFA', user }
 *     — User is already enrolled in an authenticator app. Caller asks
 *       for the 6-digit code and passes it to respondToMfaChallenge().
 *   { challenge: 'SMS_MFA', user, codeDeliveryDetails }
 *     — SMS-based fallback (we don't enable SMS by default, but the
 *       branch is here in case the pool is reconfigured later).
 *   { challenge: 'SELECT_MFA_TYPE', user, allowed }
 *     — Pool has multiple MFA methods enabled; caller picks one and
 *       calls selectMfaType().
 *
 * Throws on bad credentials, locked account, etc.
 */
export async function login(email, password) {
  const pool = await getPool();
  const user = new CognitoUser({ Username: email, Pool: pool });
  const auth = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => resolve({ ok: true, session }),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        // First-time sign-in. Caller must complete via completeNewPassword.
        resolve({ challenge: 'NEW_PASSWORD_REQUIRED', user });
      },
      // MFA enrollment required — user hasn't set up TOTP yet but the
      // pool now requires it. Hand back the user so the caller can
      // pull the secret via beginMfaSetup() and walk the customer
      // through QR-scan + verify.
      mfaSetup: () => {
        resolve({ challenge: 'MFA_SETUP', user });
      },
      // User has an authenticator app linked. Show the 6-digit prompt
      // and route the response through respondToMfaChallenge().
      totpRequired: () => {
        resolve({ challenge: 'SOFTWARE_TOKEN_MFA', user });
      },
      // SMS MFA — if SMS gets enabled later. The library hands us the
      // delivery details so we can surface "code sent to +1 ***-1234".
      mfaRequired: (challengeName, parameters) => {
        resolve({
          challenge: 'SMS_MFA',
          user,
          codeDeliveryDetails: parameters && parameters.CODE_DELIVERY_DESTINATION
            ? { destination: parameters.CODE_DELIVERY_DESTINATION }
            : null,
        });
      },
      // Multiple MFA methods enabled on the pool — user picks one.
      selectMFAType: (challengeName, parameters) => {
        resolve({
          challenge: 'SELECT_MFA_TYPE',
          user,
          allowed: parameters && parameters.MFAS_CAN_CHOOSE
            ? parameters.MFAS_CAN_CHOOSE
            : ['SOFTWARE_TOKEN_MFA'],
        });
      },
    });
  });
}

/* Kick off TOTP enrollment for a user that hit MFA_SETUP. Returns
 * { secretCode, otpAuthUri } — the secret is what the user types into
 * their authenticator app manually; the otpAuthUri is what we encode
 * as a QR code for the scan-friendly flow.
 *
 * `accountLabel` is the human-readable string the authenticator app
 * shows next to the code (e.g. the user's email address).
 * `issuer` is the brand string (defaults to "Heaven Sent").
 */
export async function beginMfaSetup(user, accountLabel, issuer = 'Heaven Sent') {
  return new Promise((resolve, reject) => {
    user.associateSoftwareToken({
      associateSecretCode: (secretCode) => {
        const label = encodeURIComponent(accountLabel || 'admin');
        const iss = encodeURIComponent(issuer);
        const otpAuthUri =
          `otpauth://totp/${iss}:${label}?secret=${secretCode}&issuer=${iss}`;
        resolve({ secretCode, otpAuthUri });
      },
      onFailure: (err) => reject(err),
    });
  });
}

/* Finalize TOTP enrollment: customer scanned the QR, app shows a
 * 6-digit code, they enter it. Cognito verifies the code matches the
 * secret it generated, then completes the original sign-in. Returns
 * { ok: true, session } on success.
 *
 * `friendlyName` shows up in the AWS Cognito console as the device
 * name; defaults to "Authenticator".
 */
export async function verifyMfaSetup(user, code, friendlyName = 'Authenticator') {
  return new Promise((resolve, reject) => {
    user.verifySoftwareToken(code, friendlyName, {
      onSuccess: (session) => resolve({ ok: true, session }),
      onFailure: (err) => reject(err),
    });
  });
}

/* Respond to an already-enrolled MFA challenge (SOFTWARE_TOKEN_MFA
 * or SMS_MFA). `challenge` matches the value returned by login().
 */
export async function respondToMfaChallenge(user, code, challenge) {
  // sendMFACode's third arg is 'SOFTWARE_TOKEN_MFA' for TOTP; omit
  // it (or pass undefined) for SMS.
  const mfaType = challenge === 'SOFTWARE_TOKEN_MFA' ? 'SOFTWARE_TOKEN_MFA' : undefined;
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve({ ok: true, session }),
        onFailure: (err) => reject(err),
      },
      mfaType,
    );
  });
}

/* When the pool has multiple MFA methods enabled, the user must pick
 * one. `chosen` is one of the values from `allowed` returned by login().
 * Resolves to a new challenge object the caller can route on
 * (typically SOFTWARE_TOKEN_MFA after picking TOTP).
 */
export async function selectMfaType(user, chosen) {
  return new Promise((resolve, reject) => {
    user.sendMFASelectionAnswer(chosen, {
      mfaSetup: () => resolve({ challenge: 'MFA_SETUP', user }),
      totpRequired: () => resolve({ challenge: 'SOFTWARE_TOKEN_MFA', user }),
      mfaRequired: (challengeName, parameters) => resolve({
        challenge: 'SMS_MFA',
        user,
        codeDeliveryDetails: parameters && parameters.CODE_DELIVERY_DESTINATION
          ? { destination: parameters.CODE_DELIVERY_DESTINATION }
          : null,
      }),
      onFailure: (err) => reject(err),
    });
  });
}

/* Complete a first-time-login flow by setting a permanent password. */
export async function completeNewPassword(user, newPassword) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      // No additional required attributes — email is the only thing
      // we collect on signup, and Cognito has it from the invitation.
      {},
      {
        onSuccess: (session) => resolve({ ok: true, session }),
        onFailure: (err) => reject(err),
      }
    );
  });
}

/* Read the ID token for the current session. The cognito-identity-js
 * library caches the session in localStorage under a pool-specific key
 * (CognitoIdentityServiceProvider.<clientId>.<username>.idToken etc),
 * so refreshes pick it up automatically.
 *
 * Returns null if there's no session OR the token has expired.
 */
export function getIdToken() {
  // Synchronous read of the cached session — Cognito stores tokens
  // in localStorage under deterministic keys so we can short-circuit
  // here without awaiting getPool().
  const pool = _cachedPool;
  if (!pool) return null;
  const user = pool.getCurrentUser();
  if (!user) return null;

  let idToken = null;
  user.getSession((err, session) => {
    if (err || !session || !session.isValid()) return;
    idToken = session.getIdToken().getJwtToken();
  });
  return idToken;
}

export function isLoggedIn() {
  return Boolean(getIdToken());
}

/* Async getter that triggers refresh if the session is expired but
 * still has a valid refresh token. Use this on app start; getIdToken()
 * is for the synchronous hot path inside fetch() helpers.
 */
export async function getValidIdToken() {
  const pool = await getPool();
  const user = pool.getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err, session) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

/* Sign out the current user, locally + at Cognito. */
export async function signOut() {
  const pool = _cachedPool || (await getPool());
  const user = pool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

/* Self-serve password reset — step 1: send a code to the user's email. */
export async function forgotPassword(email) {
  const pool = await getPool();
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve({ ok: true }),
      onFailure: (err) => reject(err),
      inputVerificationCode: () => resolve({ codeSent: true }),
    });
  });
}

/* Self-serve password reset — step 2: enter the code + new password. */
export async function confirmForgotPassword(email, code, newPassword) {
  const pool = await getPool();
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve({ ok: true }),
      onFailure: (err) => reject(err),
    });
  });
}
